// Finance board -> Referrals tab (Aug 20, 2026)
//
// The money view of the customer referral program: $250 per referral whose
// roof actually gets built. Since Oct 20, 2026 (Greg) a fee approves itself
// once the referred roof is built and that job's first payment is in; finance
// can approve one sooner by hand. Shannon pays.
//
// Deliberately NOT a second work queue — the RM-facing follow-up lives on
// /referrals. This tab is the ledger: what's owed, what's approved, what's
// been paid, and what's still in flight as future liability.
//
// Both surfaces render from src/lib/referrals.ts so the statuses, the fee, and
// the approve/pay order can't drift between them.

import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import { useRealtimeTable } from "../../lib/useRealtimeTable";
import DealLink from "../DealLink";
import {
  type Referral, REFERRAL_FEE, PAYOUT_LABELS, PAYOUT_CLASSES,
  fetchReferrals, approvePayout, markPayoutPaid, voidPayout,
  referrerAddress, referredAddress, money,
} from "../../lib/referrals";

const FINANCE_ROLES = new Set(["admin", "sales_director", "finance"]);

type DealLite = { id: string; homeowner_name: string | null; stage: string | null;
                  build_completed_date: string | null; post_build_balance_remaining: number | string | null };

export default function ReferralsFinanceTab() {
  const { role, isSuperUser } = useAuth();
  const canAct = isSuperUser || FINANCE_ROLES.has(role ?? "");

  const [rows, setRows] = useState<Referral[]>([]);
  const [deals, setDeals] = useState<Record<string, DealLite>>({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setErr(null);
      const refs = await fetchReferrals();
      setRows(refs);
      const ids = [...new Set(refs.flatMap((r) =>
        [r.referrer_deal_id, r.referred_deal_id].filter(Boolean) as string[]))];
      if (ids.length) {
        const { data } = await supabase.from("deals")
          .select("id, homeowner_name, stage, build_completed_date, post_build_balance_remaining").in("id", ids);
        setDeals(Object.fromEntries(((data ?? []) as DealLite[]).map((d) => [d.id, d])));
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't load referrals.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { void load(); }, [load]);
  useRealtimeTable("referrals", () => { void load(); });

  // Duplicate claims sit at 'earned' on purpose -- so Chad can SEE them -- but
  // they are not owed. Keeping them out of `earned` keeps the Owed total honest.
  const earned     = useMemo(() =>
    rows.filter((r) => r.payout_status === "earned" && !r.superseded_by_referral_id), [rows]);
  const duplicates = useMemo(() =>
    rows.filter((r) => r.payout_status === "earned" && r.superseded_by_referral_id), [rows]);
  const nameOf = useMemo(() => {
    const m = new Map(rows.map((r) => [r.id, r.referrer_name]));
    return (id: string | null) => (id ? m.get(id) ?? null : null);
  }, [rows]);
  const approved = useMemo(() => rows.filter((r) => r.payout_status === "approved"), [rows]);
  const paid     = useMemo(() => rows.filter((r) => r.payout_status === "paid"), [rows]);
  // Future liability: submitted or working, hasn't built yet.
  const inFlight = useMemo(() => rows.filter((r) =>
    r.payout_status === "pending" && r.status !== "no_thanks" && r.status !== "unqualified"), [rows]);

  const sum = (list: Referral[]) => list.reduce((s, r) => s + Number(r.payout_amount ?? 0), 0);
  const paidThisYear = paid.filter((r) =>
    r.payout_date && new Date(r.payout_date).getFullYear() === new Date().getFullYear());

  if (loading) return <p className="text-sm text-slate-400 p-4">Loading referrals…</p>;

  return (
    <div className="space-y-4">
      {err && <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">{err}</div>}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Owed — not yet approved" value={money(sum(earned))} sub={`${earned.length} waiting on a first payment`}
          cls={earned.length ? "border-amber-300 bg-amber-50" : "border-slate-200"} />
        <Tile label="Approved — ready to pay" value={money(sum(approved))} sub={`${approved.length} waiting on payment`}
          cls={approved.length ? "border-sky-300 bg-sky-50" : "border-slate-200"} />
        <Tile label="Paid this year" value={money(sum(paidThisYear))} sub={`${paidThisYear.length} paid`}
          cls="border-emerald-200 bg-emerald-50/40" />
        <Tile label="In flight" value={money(sum(inFlight))} sub={`${inFlight.length} not yet built · ${inFlight.filter((r) => r.claim_filed_at).length} claim filed`}
          cls="border-slate-200" />
      </div>

      {duplicates.length > 0 && (
        <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4">
          <h3 className="font-bold text-amber-900 text-sm">
            ⚠ Duplicate claims — void these ({duplicates.length})
          </h3>
          <p className="text-xs text-amber-800 mt-0.5 mb-3">
            Another customer referred the same homeowner first and wins the fee. Only the
            first referrer is paid — void these so they stop showing as earned. (Approving
            one is blocked server-side.)
          </p>
          <div className="space-y-2">
            {duplicates.map((r) => (
              <Row key={r.id} r={r} deals={deals} canAct={canAct} onChanged={load}
                mode="void" winnerName={nameOf(r.superseded_by_referral_id)} />
            ))}
          </div>
        </div>
      )}

      <Section title="Owed — awaiting approval" hint="The referred roof is built. Each fee approves on its own once that job's first payment is in; approve one now to pay it sooner."
        empty="Nothing waiting on approval." rows={earned}>
        {(r) => (
          <Row key={r.id} r={r} deals={deals} canAct={canAct} onChanged={load} mode="approve" />
        )}
      </Section>

      <Section title="Approved — ready to pay" hint="Cut the check and mark it paid to close the loop."
        empty="Nothing approved and unpaid." rows={approved}>
        {(r) => <Row key={r.id} r={r} deals={deals} canAct={canAct} onChanged={load} mode="pay" />}
      </Section>

      <Section title="In flight" hint="Referrals working through the pipeline. A filed claim means it's a real job; the fee is still owed only once the roof is built."
        empty="No referrals in flight." rows={inFlight} collapsible>
        {(r) => <Row key={r.id} r={r} deals={deals} canAct={false} onChanged={load} mode="none" />}
      </Section>

      <Section title="Paid" hint="" empty="No referral fees paid yet." rows={paid} collapsible>
        {(r) => <Row key={r.id} r={r} deals={deals} canAct={false} onChanged={load} mode="none" />}
      </Section>
    </div>
  );
}

function Tile({ label, value, sub, cls }: { label: string; value: string; sub: string; cls: string }) {
  return (
    <div className={`rounded-xl border-2 p-3 ${cls}`}>
      <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{label}</p>
      <p className="text-xl font-bold text-bardin-ink mt-1">{value}</p>
      <p className="text-xs text-slate-500">{sub}</p>
    </div>
  );
}

function Section({
  title, hint, empty, rows, collapsible, children,
}: {
  title: string; hint: string; empty: string; rows: Referral[];
  collapsible?: boolean; children: (r: Referral) => ReactNode;
}) {
  const [open, setOpen] = useState(!collapsible);
  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <button onClick={() => collapsible && setOpen(!open)}
        className={`w-full flex items-center justify-between px-4 py-3 ${collapsible ? "cursor-pointer" : "cursor-default"}`}>
        <div className="text-left">
          <h3 className="font-bold text-bardin-ink text-sm">
            {title} <span className="text-slate-400 font-normal">({rows.length})</span>
          </h3>
          {hint && open && <p className="text-xs text-slate-500 mt-0.5">{hint}</p>}
        </div>
        {collapsible && <span className="text-slate-400 text-sm">{open ? "▾" : "▸"}</span>}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-2">
          {rows.length === 0
            ? <p className="text-xs text-slate-400">{empty}</p>
            : rows.map(children)}
        </div>
      )}
    </div>
  );
}

function Row({
  r, deals, canAct, onChanged, mode, winnerName,
}: {
  r: Referral; deals: Record<string, DealLite>; canAct: boolean;
  onChanged: () => void; mode: "approve" | "pay" | "none" | "void";
  winnerName?: string | null;
}) {
  const [busy, setBusy] = useState(false);
  const [paying, setPaying] = useState(false);
  const [method, setMethod] = useState("check");
  const [ref, setRef] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [confirmOverBal, setConfirmOverBal] = useState(false);

  const referredDeal = r.referred_deal_id ? deals[r.referred_deal_id] : null;
  // build_completed_date means BUILT, not COLLECTED. Soft gate (Greg, Aug 20).
  const outstanding = Number(referredDeal?.post_build_balance_remaining ?? 0);
  const hasBalance = outstanding > 0;

  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setErr(null);
    try { await fn(); onChanged(); }
    catch (e) { setErr(e instanceof Error ? e.message : "Action failed."); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-bardin-ink">
            {money(r.payout_amount ?? REFERRAL_FEE)} → {r.referrer_name}
            <span className={`ml-2 text-[11px] px-2 py-0.5 rounded-full font-semibold ${PAYOUT_CLASSES[r.payout_status]}`}>
              {PAYOUT_LABELS[r.payout_status]}
            </span>
          </p>
          {referrerAddress(r) && (
            <p className="text-xs text-slate-500 mt-0.5">Mail to {referrerAddress(r)}</p>
          )}
          <p className="text-xs text-slate-500 mt-0.5">
            For referring <span className="font-medium">{r.referred_name}</span>
            {referredAddress(r) && ` · ${referredAddress(r)}`}
            {referredDeal && (
              <> · <DealLink id={referredDeal.id} className="text-bardin-teal underline font-medium">
                open job
              </DealLink>
              {referredDeal.build_completed_date && ` · built ${referredDeal.build_completed_date}`}</>
            )}
          </p>
          {mode === "approve" && hasBalance && (
            <p className="text-xs text-amber-800 mt-1 font-medium">
              ⚠ {money(outstanding)} still outstanding on this job
              {referredDeal?.stage === "delinquent" && " — delinquent"}. Fee is earned at
              build completion, not collection.
            </p>
          )}
          {mode === "void" && (
            <p className="text-xs text-amber-800 mt-1 font-medium">
              {winnerName ?? "Another customer"} referred {r.referred_name} first and wins the fee.
            </p>
          )}
          {r.duplicate_of_deal_id && (
            <p className="text-xs text-amber-700 mt-1 font-medium">
              {r.referred_deal_id
                ? "⚠ Address was already in the CRM — paid per policy, worth a look."
                : "⚠ Address was already in the CRM and isn't linked to that job yet — a manager links it on the Referrals board so the fee follows its build."}
            </p>
          )}
          {r.payout_status === "paid" && (
            <p className="text-xs text-emerald-700 mt-1">
              Paid {r.payout_date}{r.payout_method ? ` · ${r.payout_method}` : ""}
              {r.payout_reference ? ` #${r.payout_reference}` : ""}
            </p>
          )}
        </div>

        {canAct && mode === "void" && (
          <button disabled={busy} onClick={() => run(() => voidPayout(r.id,
            `Duplicate claim — ${winnerName ?? "another customer"} referred ${r.referred_name} first`))}
            className="px-3 py-1.5 rounded-lg bg-slate-700 text-white text-xs font-semibold disabled:opacity-50">
            Void duplicate
          </button>
        )}

        {canAct && mode === "approve" && (
          <div className="flex gap-2">
            {hasBalance && !confirmOverBal ? (
              <button disabled={busy} onClick={() => setConfirmOverBal(true)}
                className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-semibold disabled:opacity-50">
                ⚠ Approve anyway
              </button>
            ) : hasBalance && confirmOverBal ? (
              <>
                <button disabled={busy} onClick={() => run(async () => {
                  await approvePayout(r.id,
                    `Approved with ${money(outstanding)} outstanding on the referred job`, outstanding);
                  setConfirmOverBal(false);
                })} className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-semibold disabled:opacity-50">
                  Confirm over {money(outstanding)}
                </button>
                <button onClick={() => setConfirmOverBal(false)} className="px-2 py-1.5 text-xs text-slate-500">
                  Cancel
                </button>
              </>
            ) : (
              <button disabled={busy} onClick={() => run(() => approvePayout(r.id))}
                className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold disabled:opacity-50">
                ✓ Approve
              </button>
            )}
            <button disabled={busy} onClick={() => run(async () => {
              const reason = window.prompt("Why is this fee not being paid?");
              if (reason) await voidPayout(r.id, reason);
            })} className="px-3 py-1.5 rounded-lg border border-slate-300 text-xs font-semibold text-slate-600 disabled:opacity-50">
              Don’t pay
            </button>
          </div>
        )}

        {canAct && mode === "pay" && !paying && (
          <button onClick={() => setPaying(true)}
            className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold">
            💵 Mark paid
          </button>
        )}
      </div>

      {canAct && mode === "pay" && paying && (
        <div className="mt-2 flex gap-2 flex-wrap items-center">
          <select value={method} onChange={(e) => setMethod(e.target.value)}
            className="border border-slate-300 rounded-lg px-2 py-1.5 text-xs">
            <option value="check">Check</option>
            <option value="ach">ACH</option>
            <option value="cash">Cash</option>
            <option value="gift_card">Gift card</option>
            <option value="other">Other</option>
          </select>
          <input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Check # / reference"
            className="flex-1 min-w-[140px] border border-slate-300 rounded-lg px-2 py-1.5 text-xs" />
          <button disabled={busy} onClick={() => run(async () => {
            await markPayoutPaid(r.id, method, ref); setPaying(false);
          })} className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold disabled:opacity-50">
            Confirm paid
          </button>
          <button onClick={() => setPaying(false)} className="px-2 py-1.5 text-xs text-slate-500">Cancel</button>
        </div>
      )}

      {err && <p className="mt-1 text-xs text-red-600">{err}</p>}
    </div>
  );
}
